import { Platform } from './platform';
/**
 * Generate a random client secret
 * @returns a random client secret
 */
export function randomClientSecret() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
    let secret = '';
    for (let i = 0; i < 50; i++) {
        secret += chars[Math.floor(Math.random() * chars.length)];
    }
    return secret;
}
/**
 * Generate a random client id
 * @returns a random client id
 * @example
 * randomClientId(); // 'a1b2c3d4-e5f6-g7h8-i9j0-k1l2m3n4o5p6'
 */
export function randomClientId() {
    return generateUuid();
}
/**
 * Generate a random uuid
 * @returns a random uuid
 * @see https://stackoverflow.com/a/2117523/8062659
 * @example
 * generateUuid(); // 'a1b2c3d4-e5f6-g7h8-i9j0-k1l2m3n4o5p6'
 */
export function generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
/**
 * Convert a decimal number to a hexadecimal string
 * @param dec a decimal number
 * @returns a hexadecimal string
 * @see https://stackoverflow.com/a/57805/8062659
 * @example
 * dec2hex(10); // '0a'
 * dec2hex(255); // 'ff'
 * dec2hex(0); // '00'
 */
export function dec2hex(dec) {
    return ('0' + dec.toString(16)).substring(-2);
}
/**
 * Generate a random string
 * @returns a random string
 * @see https://tools.ietf.org/html/rfc7636#section-4.1
 * @example
 * generateRandomString(); // 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0'
 */
export function webGenerateRandomString() {
    // https://tools.ietf.org/html/rfc7636#section-4.1
    const array = new Uint32Array(56 / 2);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec2hex).join('');
}
/**
 * Generate a random string
 * @returns a random string
 * @see https://tools.ietf.org/html/rfc7636#section-4.1
 * @example
 * generateRandomString(); // 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0'
 */
export function generateRandomString() {
    // window.crypto is not available in Node.js
    if (typeof window !== 'undefined' && window.crypto) {
        return webGenerateRandomString();
    }
    return Math.random().toString(36).substring(2);
}
/**
 * Converts a string to base64url encoding.
 * @example
 * base64UrlEncode('Hello World!'); // 'SGVsbG8gV29ybGQh'
 */
export async function sha256Web(input) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
/**
 * Hash a string using SHA-256
 * @param input a string
 * @returns sha256 hash of the string
 * @see https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 * @example
 * sha256('Hello World!'); // '2ef7bde608ce5404e97d5f042f95f89f1c232871'
 */
export async function sha256(input) {
    if (Platform.OS === 'web') {
        return sha256Web(input);
    }
    throw new Error('Not implemented');
}
/**
 * Generate an user name from an email address
 * @param email an email address
 * @returns a user name
 * @example
 * usernameFromEmail('email@example.com'); // 'email'
 */
export function usernameFromEmail(email) {
    // Extract the part of the email before the @ symbol
    const username = email.split('@')[0];
    // Remove any non-alphanumeric characters
    const sanitizedUsername = username.replace(/\W/g, '');
    return sanitizedUsername;
}
/**
 * Increment a version number
 * @param version a version number
 * @returns the incremented version number
 * @example
 * incrementVersion('1.0.0'); // '1.0.1'
 */
export function incrementVersion(version) {
    const versionParts = version.split('.');
    if (versionParts.length !== 3) {
        throw new Error(`Invalid version ${version}`);
    }
    const major = parseInt(versionParts[0]);
    const minor = parseInt(versionParts[1]);
    const patch = parseInt(versionParts[2]);
    if (isNaN(major) || isNaN(minor) || isNaN(patch)) {
        throw new Error(`Invalid version ${version}`);
    }
    return `${major}.${minor}.${patch + 1}`;
}
/**
 * Create segments from a string
 * @param str a string
 * @param separator a separator
 * @returns segments of the string
 * @example
 * segments('Hello World!'); // ['Hello', 'World!']
 * segments('Hello World!', ' '); // ['Hello', 'World!']
 * segments('Hello World!', / /); // ['Hello', 'World!']
 */
export function segments(str, separator = /[_\- ]/) {
    return str.split(separator).filter(Boolean);
}
/**
 * Convert a string to title case
 * @param str a string
 * @returns a title case string
 * @example
 * toTitleCase('hello world!'); // 'Hello World!'
 */
export function toTitleCase(str) {
    return segments(str)
        .map((word) => word[0].toUpperCase() + word.substring(1).toLowerCase())
        .join(' ');
}
/**
 * Convert a string to camel case
 * @param str a string
 * @returns a camel case string
 * @example
 * toPascalCase('hello world!'); // 'HelloWorld'
 */
export function toPascalCase(str) {
    return segments(str)
        .map((word) => word[0].toUpperCase() + word.substring(1).toLowerCase())
        .join('');
}
/**
 * Convert a string to word case
 * @param str a string
 * @returns a word case string
 * @example
 * toWordCase('hello world!'); // 'hello world!'
 */
export function toWordCase(str) {
    return segments(str)
        .map((word) => word.toLowerCase())
        .join(' ');
}
/**
 * Analyze the frequency of thousands and decimal separators
 * @param numbers an array of numbers strings
 * @returns the most frequent thousands and decimal separators
 * @example
 * analyzeSeparators(['1,000.00', '1,000.00']); // { thousandsSeparator: ',', decimalSeparator: '.' }
 */
export function analyzeSeparators(numbers) {
    const separatorFrequency = {
        thousands: { ',': 0, '.': 0, ' ': 0 },
        decimal: { ',': 0, '.': 0 },
    };
    numbers.forEach((n) => {
        separatorFrequency.thousands[','] += (n.match(/,/g) || []).length;
        separatorFrequency.thousands['.'] += (n.match(/\./g) || []).length;
        separatorFrequency.thousands[' '] += (n.match(/ /g) || []).length;
        separatorFrequency.decimal[','] += (n.match(/,\d{1,2}(\D|$)/g) || []).length;
        separatorFrequency.decimal['.'] += (n.match(/\.\d{1,2}(\D|$)/g) || []).length;
    });
    // Determine the most frequent separators
    const thousandsSeparator = Object.keys(separatorFrequency.thousands).reduce((a, b) => separatorFrequency.thousands[a] >
        separatorFrequency.thousands[b]
        ? a
        : b);
    const decimalSeparator = Object.keys(separatorFrequency.decimal).reduce((a, b) => separatorFrequency.decimal[a] >
        separatorFrequency.decimal[b] ||
        a !== thousandsSeparator
        ? a
        : b);
    return { thousandsSeparator, decimalSeparator };
}
/**
 * Normalize a number string
 * @param separators thousands and decimal separators
 * @param n a number string
 * @returns a normalized number string
 * @example
 * normalizeNumber({ thousandsSeparator: '.', decimalSeparator: ',' }, '1.000,00'); // '1,000.00'
 * normalizeNumber({ thousandsSeparator: ',', decimalSeparator: '.' }, '1,000.00'); // '1,000.00'
 * normalizeNumber({ thousandsSeparator: ' ', decimalSeparator: ',' }, '1 000,00'); // '1,000.00'
 */
export function normalizeNumber(separators, n) {
    const decimalSeparatorRegex = new RegExp(`\\${separators.decimalSeparator}`);
    const [int, dec] = n.split(decimalSeparatorRegex);
    let normalized = int.replace(new RegExp(`\\${separators.thousandsSeparator}`, 'g'), ',');
    if (dec) {
        normalized += `.${dec}`;
    }
    return normalized;
}
/**
 * Process format pattern
 * @param defaultValue original value
 * @param value current value
 * @param formatPattern format pattern
 * @param align text input direction
 * @param showValueOnly show value only
 * @returns processed value
 * @example
 ** // Card numbers:
 * const cardNumber = 1234567812345678;
 * const cardNumberPattern = '0000 0000 0000 0000';
 * const formattedCardNumber = processPattern(cardNumber, cardNumber.toString(), cardNumberPattern, 'right');
 * console.log(formattedCardNumber); // Outputs: "1234 5678 1234 5678"
 *
 * // Currency:
 * const amount = 1234.56;
 * const currencyPattern = '0,000.00';
 * const formattedAmount = processPattern(amount, amount.toString(), currencyPattern, 'right');
 * console.log(formattedAmount); // Outputs: "1,234.56"
 *
 * // Date time format:
 * const date = new Date();
 * // Convert date to a string in the format 'YYYYMMDDHHmmss'
 * const dateString = date.toISOString().replace(/[^0-9]/g, '').slice(0, 14);
 * const datePattern = '0000-00-00 00:00:00';
 * const formattedDate = processPattern(dateString, dateString, datePattern, 'right');
 * console.log(formattedDate); // Outputs: "2022-03-01 12:00:00" (for example)
 */
export function processPattern(defaultValue, value, formatPattern, align, showValueOnly) {
    if (!formatPattern) {
        return value || defaultValue.toString() || '';
    }
    else if (!value && !defaultValue?.toString() && /^\d/.test(formatPattern)) {
        return formatPattern[0];
    }
    let inputValue;
    if (typeof defaultValue === 'number') {
        const decimalIndex = formatPattern.indexOf('.');
        if (decimalIndex !== -1) {
            const decimalPlaces = formatPattern.length - decimalIndex - 1;
            inputValue = defaultValue.toFixed(decimalPlaces);
        }
        else {
            inputValue = defaultValue.toString();
        }
    }
    else {
        inputValue = defaultValue;
    }
    let newValue;
    if (align === 'right') {
        const alphanumeric = inputValue.replace(/\W/g, '');
        let rawValueIndex = 0;
        newValue = formatPattern.split('');
        for (let i = formatPattern.length - 1; i >= 0; i--) {
            // If the pattern character is a digit, replace it with a digit from the input
            if (/\d/.test(formatPattern[i])) {
                if (rawValueIndex < alphanumeric.length &&
                    /\d/.test(alphanumeric[alphanumeric.length - 1 - rawValueIndex])) {
                    newValue[i] = alphanumeric[alphanumeric.length - 1 - rawValueIndex];
                    rawValueIndex++;
                }
                else if (showValueOnly) {
                    newValue[i] = '';
                }
            }
            // If the pattern character is a letter, replace it with a letter from the input
            else if (/[a-zA-Z]/.test(formatPattern[i])) {
                if (rawValueIndex < alphanumeric.length &&
                    /[a-zA-Z]/.test(alphanumeric[alphanumeric.length - 1 - rawValueIndex])) {
                    newValue[i] = alphanumeric[alphanumeric.length - 1 - rawValueIndex];
                    rawValueIndex++;
                }
                else if (showValueOnly) {
                    newValue[i] = '';
                }
            }
            // If the pattern character is not alphanumeric, only add it to newValue if there is a corresponding input character
            else {
                if (rawValueIndex > 0 && rawValueIndex <= alphanumeric.length) {
                    // Do nothing, keep the format character
                }
                else if (showValueOnly) {
                    newValue[i] = '';
                }
            }
        }
        // Join the characters into a string and trim leading commas
        newValue = newValue.join('').replace(/^,+/, '');
    }
    else {
        newValue = '';
        let patternIndex = 0;
        for (let i = 0; i < inputValue.length; i++) {
            const char = inputValue[i];
            // Skip non-alphanumeric characters in pattern
            while (patternIndex < formatPattern.length &&
                !/\w/.test(formatPattern[patternIndex]) &&
                (!showValueOnly || i === value.length - 1)) {
                newValue += formatPattern[patternIndex];
                patternIndex++;
            }
            if (patternIndex >= formatPattern.length) {
                break;
            }
            // Check if character matches pattern
            if ((/\d/.test(formatPattern[patternIndex]) && /\d/.test(char)) ||
                (/[a-zA-Z]/.test(formatPattern[patternIndex]) && /[a-zA-Z]/.test(char))) {
                newValue += char;
                patternIndex++;
            }
        }
    }
    if (newValue === formatPattern) {
        newValue = '';
    }
    return newValue;
}
//# sourceMappingURL=data:application/json;base64,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